import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LandlordTriggers — Hands-free automated-alert panel for the Cockpit.
 *
 * Scans the property intelligence stream via /api/property/triggers and
 * fires action-ready alerts when compliance clocks, bond schedules, or
 * housing program flags cross owner-visible thresholds. Every trigger
 * carries a deep-link into the correct workflow so the owner never
 * has to hunt.
 *
 * Zero manual entry. Address defaults to the most recently touched
 * project address (stored by Design Studio in sessionStorage).
 */

const SEVERITY_STYLE = {
  critical: { color: "#F87171", bg: "rgba(248,113,113,0.10)", ring: "rgba(248,113,113,0.42)" },
  warning:  { color: "#FFB347", bg: "rgba(255,179,71,0.10)",  ring: "rgba(255,179,71,0.42)"  },
  info:     { color: "#5AC8D8", bg: "rgba(90,200,216,0.08)",  ring: "rgba(90,200,216,0.32)"  },
};

const LandlordTriggers = () => {
  const [addressInput, setAddressInput] = useState("");
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pulledAt, setPulledAt] = useState("");

  // Auto-load the last address the owner touched in Design Studio
  useEffect(() => {
    try {
      const last = sessionStorage.getItem("mf_last_address");
      if (last) setAddressInput(last);
    } catch (_) { /* ignore */ }
  }, []);

  const scan = async (addr) => {
    const a = (addr ?? addressInput).trim();
    if (!a) { setTriggers([]); return; }
    setLoading(true);
    try {
      const q = encodeURIComponent(a);
      const r = await fetch(`${BACKEND_URL}/api/property/triggers?address=${q}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setTriggers(d.triggers || []);
        setPulledAt(d.pulled_at || "");
      }
    } catch (_) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => {
    if (addressInput.trim()) scan(addressInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="landlord-triggers" style={{
      background: "linear-gradient(180deg, rgba(167,139,250,0.05) 0%, rgba(0,255,255,0.03) 100%)",
      border: "1px solid rgba(167,139,250,0.28)",
      borderRadius: 12,
      padding: "14px 16px 16px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ color: "#A78BFA", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.2em", fontWeight: 800 }}>
            &gt; LANDLORD AUTOMATED TRIGGERS
          </div>
          <div style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 800, marginTop: 3 }}>
            Compliance clock · bond calendar · program flags
          </div>
        </div>
        {pulledAt && (
          <div style={{ color: "#7E92A8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.14em" }}>
            LAST SCAN · {new Date(pulledAt).toLocaleTimeString()}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <input
          type="text"
          data-testid="landlord-triggers-address"
          value={addressInput}
          placeholder="Property address or owner name…"
          onChange={(e) => {
            setAddressInput(e.target.value);
            try { sessionStorage.setItem("mf_last_address", e.target.value); } catch (_) { /* ignore */ }
          }}
          onKeyDown={(e) => { if (e.key === "Enter") scan(addressInput); }}
          style={{
            flex: 1, minWidth: 180,
            background: "rgba(2,10,31,0.55)", color: "#fff",
            border: "1px solid rgba(255,255,255,0.10)", borderRadius: 6,
            padding: "8px 12px", fontSize: "0.85rem",
          }}
        />
        <button
          type="button"
          data-testid="landlord-triggers-scan"
          onClick={() => scan(addressInput)}
          disabled={loading}
          style={{
            background: "#A78BFA", color: "#03151F", border: "none",
            padding: "8px 14px", borderRadius: 6,
            fontWeight: 800, fontSize: "0.82rem", cursor: "pointer",
          }}
        >
          {loading ? "Scanning…" : "▶ Scan"}
        </button>
      </div>

      {!loading && addressInput.trim() && triggers.length === 0 && (
        <div style={{ color: "#7E92A8", fontSize: "0.82rem", padding: "0.5rem 0" }}>
          No triggers for this property. Compliance clock and bond calendar are green.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {triggers.map(t => {
          const s = SEVERITY_STYLE[t.severity] || SEVERITY_STYLE.info;
          return (
            <div key={t.id} data-testid={`landlord-trigger-${t.id}`} style={{
              background: s.bg, border: `1px solid ${s.ring}`, borderRadius: 8,
              padding: "10px 12px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <div style={{ color: s.color, fontWeight: 800, fontSize: "0.9rem" }}>
                  {t.title}
                </div>
                <span style={{
                  color: s.color, fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "0.6rem", letterSpacing: "0.14em", fontWeight: 800,
                  background: `${s.color}20`, padding: "2px 6px", borderRadius: 3,
                }}>
                  {t.severity.toUpperCase()}
                </span>
              </div>
              <div style={{ color: "#B4BEC8", fontSize: "0.78rem", marginTop: 4, lineHeight: 1.5 }}>
                {t.detail}
              </div>
              {t.action_href && (
                <a href={t.action_href}
                   data-testid={`landlord-trigger-action-${t.id}`}
                   style={{
                     display: "inline-block", marginTop: 8,
                     color: s.color, textDecoration: "none",
                     fontSize: "0.75rem", fontWeight: 800,
                     borderBottom: `1px dashed ${s.color}`,
                   }}>
                  {t.action_label} →
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LandlordTriggers;
